import React from "react";
import { Link } from "react-router-dom";
import "./FancyCards.css";
import { FaSearchDollar, FaDollarSign } from "react-icons/fa";
import {
  ArrowRight,
  Shield,
  TrendingUp,
  Globe,
  CheckCircle,
} from "lucide-react";

const cardData = [
  {
    title: "Discover & Acquire Premium .Gold Domains",
    description:
      "Explore a curated selection of exclusive .gold domains designed for prestige and growth. Find the perfect digital asset to elevate your brand and secure your place in the premium marketplace.",
    link: "/Marketplace",
    icon: FaSearchDollar,
    badge: "BUY DOMAINS",
    features: ["Verified Sellers", "Instant Purchase", "Secure Escrow"],
  },
  {
    title: "Sell Your .Gold Domain at Maximum Value",
    description:
      "Unlock the full potential of your .gold domain by listing it on our premium marketplace. Reach verified global buyers and achieve optimal returns with transparent pricing and trusted transactions.",
    link: "/post-dot-gold-domain",
    icon: FaDollarSign,
    badge: "SELL DOMAINS",
    features: ["Global Reach", "Premium Listing", "Fast Transactions"],
  },
];

function FancyCards() {
  return (
    <div className="bg-[#101c4c] py-16 md:py-10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-2 rounded-full mb-6 border border-[#fcdb6a]/30">
            <Shield className="w-4 h-4 text-[#fcdb6a]" />
            <span className="text-[#fcdb6a] font-semibold text-sm tracking-wide uppercase">
              Premium Services
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Exclusive services for{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#fcdb6a] to-[#b99653]">
              domain investors
            </span>
          </h2>

          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Empower your digital portfolio with our premium marketplace
            solutions.
          </p>
        </div>

        {/* Cards - Stacked Layout */}
        <div className="space-y-8">
          {cardData.map((card, index) => {
            const Icon = card.icon;

            return (
              <Link to={card.link} key={index} className="group block">
                <article className="relative overflow-hidden rounded-3xl bg-white border-2 border-transparent p-8 md:p-10 shadow-xl">
                  {/* Content Grid */}
                  <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                    {/* Left Side - Icon & Badge */}
                    <div className="lg:col-span-3 flex flex-col items-center lg:items-start gap-4">
                      {/* Icon */}
                      <div className="p-6 rounded-2xl shadow-lg bg-gradient-to-br from-[#fcdb6a] to-[#b99653]">
                        <Icon className="w-12 h-12 text-[#101c4c]" />
                      </div>

                      {/* Badge */}
                      <span className="px-6 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider shadow-md bg-gradient-to-r from-[#fcdb6a] to-[#b99653] text-[#101c4c]">
                        {card.badge}
                      </span>
                    </div>

                    {/* Right Side - Content */}
                    <div className="lg:col-span-9">
                      <h3 className="text-2xl sm:text-3xl font-bold leading-tight mb-4 text-[#0d1636]">
                        {card.title}
                      </h3>

                      <p className="text-base sm:text-lg leading-relaxed mb-6 text-gray-600">
                        {card.description}
                      </p>

                      {/* Features */}
                      <div className="flex flex-wrap gap-3 mb-6">
                        {card.features.map((feature, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#fcdb6a]/10"
                          >
                            <CheckCircle className="w-4 h-4 text-[#b99653]" />
                            <span className="text-sm font-semibold text-[#101c4c]">
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* CTA Button */}
                      <div className="inline-flex items-center gap-3 px-8 py-3.5 rounded-xl font-bold shadow-lg bg-gradient-to-r from-[#b99653] to-[#fcdb6a] text-white">
                        <span>Get Started Now</span>
                        <ArrowRight className="w-5 h-5" />
                      </div>
                    </div>
                  </div>
                </article>
              </Link>
            );
          })}
        </div>

        {/* Bottom Stats Section */}
        <div className="mt-16 bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#fcdb6a] to-[#b99653] mb-4 mx-auto shadow-lg">
                <Shield className="w-8 h-8 text-[#101c4c]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Secure Transactions
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Protected by industry-leading security protocols
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#fcdb6a] to-[#b99653] mb-4 mx-auto shadow-lg">
                <TrendingUp className="w-8 h-8 text-[#101c4c]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Premium Listings
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Access to exclusive high-value domain inventory
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#fcdb6a] to-[#b99653] mb-4 mx-auto shadow-lg">
                <Globe className="w-8 h-8 text-[#101c4c]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                Transparent Pricing
              </h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Fair market valuations with no hidden fees
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FancyCards;
